/**
 * Seller Gmail-style inbox (2026-11-04) — a port of etechmail.com/admin/src/screen/dashboard/pages/Inbox/Inbox.jsx
 * (same design and features) on the EtechBackend /mail API (./mailService.js). Not here: etechmail-only
 * campaign chips/tabs and the Warmup folder. Live updates: the notification bell's WebSocket
 * (window "etech-notification" event, type email) plus a 30 s refresh.
 */
import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  getThreadEmails,
  getEmails,
  getEmailById,
  sendEmail,
  replyToEmail,
  updateEmail,
  deleteEmail,
  bulkDeleteEmails,
  getEmailCounts,
  forwardEmail,
  createDraft,
  updateDraft,
  discardDraft,
  downloadAttachment,
  threadAction,
  retryDelivery,
  getEmailAccounts,
} from "./mailService";

// MUI Icons
import InboxIcon from "@mui/icons-material/Inbox";
import SendIcon from "@mui/icons-material/Send";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import MarkEmailUnreadIcon from "@mui/icons-material/MarkEmailUnread";
import SearchIcon from "@mui/icons-material/Search";
import ReplyIcon from "@mui/icons-material/Reply";
import ReplyAllIcon from "@mui/icons-material/ReplyAll";
import CloseIcon from "@mui/icons-material/Close";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import RefreshIcon from "@mui/icons-material/Refresh";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DraftsIcon from "@mui/icons-material/Drafts";
import ReportIcon from "@mui/icons-material/Report";
import RemoveIcon from "@mui/icons-material/Remove";
import EmailIcon from "@mui/icons-material/Email";
import ForwardToInboxIcon from "@mui/icons-material/ForwardToInbox";
import ArchiveIcon from "@mui/icons-material/Archive";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import MessageBody from "./MessageBody";
import { splitQuotedHtml, splitQuotedText } from "./quote";
import { sanitize } from "./sanitizeHtml";
import styles from "./Inbox.module.css";

// A reply that asked to stop (Backend utils/unsubscribe.js).
const UNSUB_TITLE = "Asked not to be emailed again. Your inboxes can no longer email this address (Cold Outreach → Unsubscribes).";

// ─── Helpers ──────────────────────────────────────────────────────────────────
const AVATAR_COLORS = [
  "#e53e3e","#dd6b20","#d69e2e","#38a169",
  "#3182ce","#805ad5","#d53f8c","#0694a2",
];
const colorFor = (seed) => {
  const s = String(seed || "?");
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
};
const initialOf = (s) => ((String(s || "?").trim()[0]) || "?").toUpperCase();
function getSenderName(email) {
  return email?.from?.name || email?.from?.email || "Unknown";
}
const recipientsOf = (e) => (e?.to || []).map(t => t.name || t.email).join(", ");
function formatDate(date) {
  const d = new Date(date);
  const diff = Date.now() - d;
  const days = Math.floor(diff / 86400000);
  if (days === 0) return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  if (days === 1) return "Yesterday";
  if (days < 7) return d.toLocaleDateString([], { weekday: "short" });
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
}
const fullDate = (date) => new Date(date).toLocaleString([], {
  year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
});
const stripHtml = (h) => String(h || "").replace(/<style[\s\S]*?<\/style>/gi, " ").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
const snippetOf = (e) => (String(e?.text || "").replace(/\s+/g, " ").trim() || stripHtml(e?.html)).slice(0, 120);
const firstName = (p) => (p.me ? "me" : (p.name || p.email || "").split(/[\s<@]/)[0] || p.email);
const PREFIX_RE = /^\s*(?:(?:re|fw|fwd|aw|sv|vs|tr|wg)\s*(?:\[\d+\])?\s*:\s*)+/i;
const conversationSubject = (s) => (String(s || "").replace(PREFIX_RE, "").trim() || "(No Subject)");
const HIDDEN_IN_CONVERSATION = ["trash", "spam"];
const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;   // TOTAL per email (EtechBackend /mail accepts up to 20 MB)
// Same blocked types as the backend: programs, scripts, installers, disk images, macro Office files.
const BLOCKED_ATTACHMENT_RE = /\.(exe|com|scr|pif|dll|cpl|msi|msp|mst|sys|drv|ocx|gadget|bat|cmd|vbs|vbe|js|jse|ws|wsf|wsc|wsh|ps1|ps1xml|ps2|psc1|psm1|hta|sct|reg|inf|scf|msc|jar|lnk|url|appref-ms|application|iso|img|vhd|vhdx|apk|app|dmg|pkg|deb|rpm|sh|command|docm|dotm|xlsm|xltm|xlam|pptm|potm|ppam|ppsm|sldm)$/i;
const attachmentName = (f) => String(f.name || "").replace(/[\u200B-\u200F\u202A-\u202E\u2066-\u2069\uFEFF]/g, "").replace(/[.\s]+$/, "");
/** Split picked files into what fits (type allowed, running total ≤ 2 MB) and why the rest was refused. */
const filterAttachments = (current, picked) => {
  let total = current.reduce((n, f) => n + (f.size || 0), 0);
  const accepted = [], blocked = [], tooBig = [];
  for (const f of picked) {
    if (BLOCKED_ATTACHMENT_RE.test(attachmentName(f))) blocked.push(f.name);
    else if (total + f.size > MAX_ATTACHMENT_BYTES) tooBig.push(f.name);
    else { accepted.push(f); total += f.size; }
  }
  const msgs = [];
  if (blocked.length) msgs.push(`This file type cannot be sent (programs, scripts and installers are blocked): ${blocked.join(", ")}`);
  if (tooBig.length) msgs.push(`Attachments are limited to 10 MB per email; not attached: ${tooBig.join(", ")}`);
  return { accepted, message: msgs.join("\n") };
};
const isPending = (s) => s === "queued" || s === "sending";

// Gmail's conversation timestamps: "5:16 AM (14 minutes ago)" today,
// "Sep 12, 2026, 10:51 AM (7 days ago)" otherwise.
const gmailDate = (date) => {
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const main = sameDay
    ? d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
    : d.toLocaleString([], { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  const mins = Math.round((now - d) / 60000);
  let rel;
  if (mins < 1) rel = "just now";
  else if (mins < 60) rel = `${mins} minute${mins === 1 ? "" : "s"} ago`;
  else if (mins < 1440) { const h = Math.round(mins / 60); rel = `${h} hour${h === 1 ? "" : "s"} ago`; }
  else { const days = Math.round(mins / 1440); rel = `${days} day${days === 1 ? "" : "s"} ago`; }
  return `${main} (${rel})`;
};

// Collapsed-row snippet without the quoted earlier message (Gmail trims it there too)
const cleanSnippet = (m) => {
  if (m?.text) return splitQuotedText(m.text).main.replace(/\s+/g, " ").trim().slice(0, 140);
  const { main } = splitQuotedHtml(sanitize(m?.html || ""));
  return stripHtml(main).slice(0, 140);
};

// ─── Main Inbox component ─────────────────────────────────────────────────────
const Inbox = () => {
  // List rows: conversations (every folder but Drafts) or single drafts
  const [rows, setRows]                   = useState([]);
  const [emailAccounts, setEmailAccounts] = useState([]);
  const [loading, setLoading]             = useState(false);
  const [refreshing, setRefreshing]       = useState(false);

  // Open conversation: { threadId, messages } — Drafts open as one message
  const [conversation, setConversation]   = useState(null);
  const [expanded, setExpanded]           = useState(new Set());
  const [details, setDetails]             = useState(new Set());
  const [showHidden, setShowHidden]       = useState(false);

  // Compose
  const [showCompose, setShowCompose]           = useState(false);
  const [composeMinimized, setComposeMinimized] = useState(false);

  // Bulk selection (thread ids, or draft ids in Drafts)
  const [selectedRows, setSelectedRows] = useState([]);

  // Inline reply — bound to one message of the open conversation
  const [replyTarget, setReplyTarget]         = useState(null); // message doc
  const [replyMode, setReplyMode]             = useState("reply"); // "reply" | "replyAll"
  const [replyText, setReplyText]             = useState("");
  const [replyAttachments, setReplyAttachments] = useState([]);
  const [sendingReply, setSendingReply]       = useState(false);
  const replyFileInputRef = useRef(null);
  const replyBoxRef = useRef(null);

  // Forward — bound to one message of the open conversation
  const [forwardTarget, setForwardTarget] = useState(null);
  const [forwardTo, setForwardTo]         = useState("");
  const [forwardNote, setForwardNote]     = useState("");
  const [sendingForward, setSendingForward] = useState(false);

  // Snackbar with optional action (Undo / Retry), Gmail-style
  const [snack, setSnack] = useState(null);
  const snackTimer = useRef(null);

  // Folder counts (unread badges from DB, not just current page)
  const [folderCounts, setFolderCounts] = useState({});
  // Unread inbox conversations per mailbox, keyed by emailAccountId
  const [accountCounts, setAccountCounts] = useState({});

  // Filters / pagination
  const [folder, setFolder]               = useState("inbox");
  const [searchQuery, setSearchQuery]     = useState("");
  const [filterRead, setFilterRead]       = useState(null);
  const [filterStarred, setFilterStarred] = useState(null);
  // Campaign conversations: 'replies' (from a campaign) | 'interested' (positive replies)
  const [filterCampaign, setFilterCampaign] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState("");
  const [page, setPage]                   = useState(1);
  const [pagination, setPagination]       = useState({ total: 0, pages: 1 });

  const searchTimer = useRef(null);
  const firstSearchRun = useRef(true);
  const threaded = folder !== "draft";

  // Conversation cache + hover prefetch: opening a row you have hovered is instant
  const convCache = useRef(new Map());
  const prefetchTimer = useRef(null);

  // Outbox: messages we are waiting on (emailId → poll timer)
  const pendingRef = useRef(new Map());
  const applyStatusRef = useRef(() => {});

  const ownAddresses = React.useMemo(
    () => new Set(emailAccounts.map(a => String(a.emailAddress).toLowerCase())),
    [emailAccounts]
  );
  const isMine = useCallback((m) => ownAddresses.has(String(m?.from?.email || "").toLowerCase()), [ownAddresses]);
  const accountLabel = (id) => emailAccounts.find(a => a._id === String(id))?.emailAddress || "";
  const acctParam = selectedAccount ? { emailAccountId: selectedAccount } : {};

  // ── Effects ────────────────────────────────────────────────────────────────
  useEffect(() => { fetchEmailAccounts(); }, []);

  useEffect(() => {
    setRefreshing(true);
    fetchRows().finally(() => setRefreshing(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folder, filterRead, filterStarred, filterCampaign, selectedAccount, page]);

  useEffect(() => {
    // The folder effect already fetched on mount; don't fetch twice.
    if (firstSearchRun.current) { firstSearchRun.current = false; return; }
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setRefreshing(true);
      fetchRows().finally(() => setRefreshing(false));
    }, 350);
    return () => clearTimeout(searchTimer.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  // Fetch folder counts on mount and refresh them whenever emails change
  const fetchFolderCounts = useCallback(async () => {
    try {
      const params = {};
      if (selectedAccount) params.emailAccountId = selectedAccount;
      const r = await getEmailCounts(params);
      if (r?.success) {
        setFolderCounts(r.data || {});
        setAccountCounts(r.byAccount || {});
      }
    } catch (e) { console.error(e); }
  }, [selectedAccount]);

  useEffect(() => { fetchFolderCounts(); }, [fetchFolderCounts]);

  // Live updates: a new email notification (bell WebSocket → window "etech-notification") refreshes the
  // list and the counts at once; a 30 s refresh catches changes made elsewhere (phone, Outlook).
  useEffect(() => {
    const onNotif = (e) => {
      if (e?.detail?.type !== "email") return;
      if (folder === "inbox") fetchRows();
      fetchFolderCounts();
    };
    window.addEventListener("etech-notification", onNotif);
    const t = setInterval(() => {
      if (document.hidden) return;
      fetchFolderCounts();
      if (!searchQuery) fetchRows();
    }, 30000);
    return () => { window.removeEventListener("etech-notification", onNotif); clearInterval(t); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folder, fetchFolderCounts, searchQuery, page, selectedAccount, filterRead, filterStarred]);

  useEffect(() => () => {
    clearTimeout(snackTimer.current);
    for (const p of pendingRef.current.values()) clearInterval(p.timer);
  }, []);

  // ── Snackbar ───────────────────────────────────────────────────────────────
  const showSnack = (text, actionFn, actionLabel = "Undo", ms) => {
    clearTimeout(snackTimer.current);
    setSnack({ text, action: actionFn ? { label: actionLabel, fn: actionFn } : null });
    snackTimer.current = setTimeout(() => setSnack(null), ms || (actionFn ? 8000 : 4000));
  };

  // ── Outbox tracking ────────────────────────────────────────────────────────
  const applyStatus = useCallback(({ emailId, status, error }) => {
    const id = String(emailId);
    const p = pendingRef.current.get(id);
    if (p?.timer) clearInterval(p.timer);
    pendingRef.current.delete(id);

    setConversation(c => c ? { ...c, messages: c.messages.map(m => String(m._id) === id ? { ...m, deliveryStatus: status, deliveryError: error || null } : m) } : c);
    setRows(prev => prev.map(r => (r.emailIds || []).some(x => String(x) === id)
      ? { ...r, latestDeliveryStatus: status, hasFailed: status === "failed", hasPending: false }
      : r));
    for (const [tid, msgs] of convCache.current) {
      if (msgs.some(m => String(m._id) === id)) convCache.current.set(tid, msgs.map(m => String(m._id) === id ? { ...m, deliveryStatus: status, deliveryError: error || null } : m));
    }

    if (status === "sent") showSnack("Message sent.");
    else if (status === "failed") showSnack(`Couldn't send: ${error || "unknown error"}`, () => retry(id), "Retry", 12000);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  applyStatusRef.current = applyStatus;

  // Poll as a fallback in case the socket is not connected
  const trackDelivery = (doc) => {
    if (!doc?._id || doc.deliveryStatus === "sent") return;
    const id = String(doc._id);
    if (pendingRef.current.has(id)) return;
    let tries = 0;
    const timer = setInterval(async () => {
      tries += 1;
      try {
        const r = await getEmailById(id);
        const s = r?.data?.deliveryStatus;
        if (r?.success && (s === "sent" || s === "failed")) { applyStatus({ emailId: id, status: s, error: r.data.deliveryError }); return; }
      } catch { /* keep polling */ }
      if (tries >= 40) { clearInterval(timer); pendingRef.current.delete(id); }
    }, 3000);
    pendingRef.current.set(id, { timer });
  };

  const retry = async (emailId) => {
    try {
      const r = await retryDelivery(emailId);
      if (!r?.success) { alert(r?.error || "Couldn't retry"); return; }
      showSnack("Sending…");
      setConversation(c => c ? { ...c, messages: c.messages.map(m => String(m._id) === String(emailId) ? { ...m, deliveryStatus: "queued", deliveryError: null } : m) } : c);
      setRows(prev => prev.map(r => (r.emailIds || []).some(x => String(x) === String(emailId)) ? { ...r, latestDeliveryStatus: "queued", hasFailed: false, hasPending: true } : r));
      trackDelivery({ _id: emailId, deliveryStatus: "queued" });
    } catch (e) { alert(e?.error || "Couldn't retry"); }
  };

  // ── Data fetchers ──────────────────────────────────────────────────────────
  const fetchEmailAccounts = async () => {
    try {
      const r = await getEmailAccounts();
      if (r?.success) setEmailAccounts(r.data || []);
    } catch (e) { console.error(e); }
  };

  const fetchRows = async () => {
    setLoading(true);
    try {
      const params = { folder, page, limit: 50 };
      if (selectedAccount) params.emailAccountId = selectedAccount;
      if (searchQuery)     { params.search = searchQuery; params.folder = "all"; }
      if (filterRead !== null)    params.isRead = filterRead;
      if (filterStarred !== null) { params.isStarred = filterStarred; if (filterStarred) params.folder = "all"; }
      if (threaded)        params.groupByThread = true;
      if (threaded && filterCampaign) params.campaign = filterCampaign;

      const r = await getEmails(params);
      if (r?.success) {
        setRows(r.data.groupByThread ? (r.data.threads || []) : (r.data.emails || []));
        setPagination(r.data.pagination || { total: 0, pages: 1 });
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const rowId = (row) => (threaded ? row.threadId : row._id);
  const rowUnread = (row) => (threaded ? row.hasUnread : !row.isRead);

  const fetchThread = async (threadId) => {
    const r = await getThreadEmails(threadId, acctParam);
    if (!r?.success) return null;
    // Warm-up mail belongs to the Warmup folder only.
    const messages = (r.data.emails || []).filter(m => (folder === "warmup") === (m.folder === "warmup"));
    convCache.current.set(threadId, messages);
    return messages;
  };

  const prefetchRow = (row) => {
    if (!threaded || convCache.current.has(row.threadId)) return;
    clearTimeout(prefetchTimer.current);
    prefetchTimer.current = setTimeout(() => { fetchThread(row.threadId).catch(() => {}); }, 150);
  };
  const cancelPrefetch = () => clearTimeout(prefetchTimer.current);

  // ── Open a conversation ────────────────────────────────────────────────────
  const openRow = async (row) => {
    try {
      setReplyTarget(null); setReplyText(""); setReplyAttachments([]);
      setForwardTarget(null); setForwardTo(""); setForwardNote("");
      setShowHidden(false);
      if (!threaded) {
        const r = await getEmailById(row._id);
        if (r?.success) {
          setConversation({ threadId: null, messages: [r.data], single: true });
          setExpanded(new Set([r.data._id]));
        }
        return;
      }

      const show = (messages) => {
        const inTrashView = folder === "trash" || folder === "spam";
        const visible = messages.filter(m => inTrashView || !HIDDEN_IN_CONVERSATION.includes(m.folder));
        // Gmail opens every unread message plus the newest one
        const open = new Set(visible.filter(m => !m.isRead).map(m => m._id));
        if (visible.length) open.add(visible[visible.length - 1]._id);
        setExpanded(open);
        setConversation({ threadId: row.threadId, messages });
        if (messages.some(m => !m.isRead)) {
          threadAction(row.threadId, "read", acctParam).then(() => fetchFolderCounts()).catch(() => {});
          setRows(prev => prev.map(t => t.threadId === row.threadId ? { ...t, hasUnread: false, unreadCount: 0 } : t));
          const read = messages.map(m => ({ ...m, isRead: true }));
          setConversation({ threadId: row.threadId, messages: read });
          convCache.current.set(row.threadId, read);
        }
      };

      const cached = convCache.current.get(row.threadId);
      if (cached) {
        show(cached);
        // revalidate quietly
        fetchThread(row.threadId).then(fresh => {
          if (fresh && fresh.length !== cached.length) setConversation(c => c && c.threadId === row.threadId ? { ...c, messages: fresh.map(m => ({ ...m, isRead: true })) } : c);
        }).catch(() => {});
        return;
      }
      const messages = await fetchThread(row.threadId);
      if (messages) show(messages);
    } catch (e) { console.error(e); }
  };

  const reloadConversation = async () => {
    if (!conversation?.threadId) return;
    try {
      const messages = await fetchThread(conversation.threadId);
      if (messages) {
        setConversation({ threadId: conversation.threadId, messages });
        if (messages.length) setExpanded(prev => new Set([...prev, messages[messages.length - 1]._id]));
      }
    } catch (e) { console.error(e); }
  };

  const closeDetail = () => {
    setConversation(null);
    setReplyTarget(null);
    setForwardTarget(null);
    setReplyMode("reply");
    setReplyText("");
    setReplyAttachments([]);
    setForwardTo("");
    setForwardNote("");
  };

  // ── Compose / reply / forward (all queued — the API answers at once) ──────
  const handleSendEmail = async (emailData) => {
    try {
      const r = await sendEmail(emailData);
      if (r?.success) {
        setShowCompose(false);
        showSnack("Sending…");
        trackDelivery(r.data);
        fetchFolderCounts();
        if (folder === "sent") fetchRows();
        return { success: true };
      }
      return { success: false, error: r?.error || "Failed to send" };
    } catch (e) {
      return { success: false, error: e?.error || "Failed to send" };
    }
  };

  const appendToConversation = (doc) => {
    setConversation(c => c ? { ...c, messages: [...c.messages, doc] } : c);
    setExpanded(prev => new Set([...prev, doc._id]));
    if (conversation?.threadId) {
      const cached = convCache.current.get(conversation.threadId);
      if (cached) convCache.current.set(conversation.threadId, [...cached, doc]);
    }
  };

  const startReply = (message, mode) => {
    setForwardTarget(null);
    setReplyTarget(message);
    setReplyMode(mode);
    setReplyText("");
    setReplyAttachments([]);
    setTimeout(() => replyBoxRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }), 50);
  };

  const handleReplyInline = async () => {
    if (!replyTarget) return;
    if (!replyText.trim() && replyAttachments.length === 0) return;
    setSendingReply(true);
    try {
      // Reply from the mailbox that holds this message (or the selected one)
      const accountId =
        selectedAccount ||
        (typeof replyTarget.emailAccountId === "object"
          ? replyTarget.emailAccountId._id
          : replyTarget.emailAccountId);

      const r = await replyToEmail({
        emailId:        replyTarget._id,
        emailAccountId: accountId,
        text:           replyText,
        html:           `<p>${replyText.replace(/\n/g, "<br/>")}</p>`,
        replyAll:       replyMode === "replyAll",
        attachments:    replyAttachments,
      });
      if (r?.success) {
        setReplyTarget(null);
        setReplyText("");
        setReplyAttachments([]);
        appendToConversation(r.data);      // shows "Sending…" until the outbox reports back
        showSnack("Sending…");
        trackDelivery(r.data);
        fetchFolderCounts();
      } else {
        alert(r?.error || "Failed to send reply");
      }
    } catch (e) {
      alert(e?.error || "Failed to send reply");
    } finally {
      setSendingReply(false);
    }
  };

  const handleReplyAttach = (e) => {
    const picked = Array.from(e.target.files || []);
    const { accepted, message } = filterAttachments(replyAttachments, picked);
    if (message) alert(message);
    setReplyAttachments(prev => [...prev, ...accepted]);
    // Reset the input so picking the same file again re-fires onChange
    if (replyFileInputRef.current) replyFileInputRef.current.value = "";
  };

  const removeReplyAttachment = (idx) => {
    setReplyAttachments(prev => prev.filter((_, i) => i !== idx));
  };

  const startForward = (message) => {
    setReplyTarget(null);
    setForwardTarget(message);
    setForwardTo("");
    setForwardNote("");
    setTimeout(() => replyBoxRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }), 50);
  };

  const handleForward = async () => {
    if (!forwardTarget || !forwardTo.trim()) return;
    setSendingForward(true);
    try {
      const accountId =
        selectedAccount ||
        (typeof forwardTarget.emailAccountId === "object"
          ? forwardTarget.emailAccountId._id
          : forwardTarget.emailAccountId);

      const toList = forwardTo.split(",").map(e => ({ email: e.trim() })).filter(e => e.email);
      const r = await forwardEmail({
        emailId:        forwardTarget._id,
        emailAccountId: accountId,
        to:             toList,
        extraText:      forwardNote,
      });
      if (r?.success) {
        setForwardTarget(null);
        setForwardTo("");
        setForwardNote("");
        appendToConversation(r.data);
        showSnack("Sending…");
        trackDelivery(r.data);
        fetchFolderCounts();
      } else {
        alert(r?.error || "Failed to forward");
      }
    } catch (e) {
      alert(e?.error || "Failed to forward");
    } finally {
      setSendingForward(false);
    }
  };

  // ── Conversation actions (rows + open conversation) ───────────────────────
  const toggleStarRow = async (row, e) => {
    e?.stopPropagation();
    const next = !row.isStarred;
    setRows(prev => prev.map(r => rowId(r) === rowId(row) ? { ...r, isStarred: next } : r));
    try {
      if (threaded) await threadAction(row.threadId, next ? "star" : "unstar", acctParam);
      else await updateEmail(row._id, { isStarred: next });
    } catch (e) {
      console.error(e);
      setRows(prev => prev.map(r => rowId(r) === rowId(row) ? { ...r, isStarred: !next } : r));
    }
  };

  const toggleStarMessage = async (m) => {
    const next = !m.isStarred;
    setConversation(c => c ? { ...c, messages: c.messages.map(x => x._id === m._id ? { ...x, isStarred: next } : x) } : c);
    try { await updateEmail(m._id, { isStarred: next }); } catch (e) { console.error(e); }
  };

  /** Move conversations between folders with Undo. */
  const moveRows = async (ids, action, verb) => {
    const targets = rows.filter(r => ids.includes(rowId(r)));
    if (!targets.length) return;
    setRows(prev => prev.filter(r => !ids.includes(rowId(r))));
    setPagination(p => ({ ...p, total: Math.max(0, (p.total || 0) - targets.length) }));
    setSelectedRows([]);
    targets.forEach(t => convCache.current.delete(t.threadId));
    try {
      const results = threaded
        ? await Promise.all(targets.map(t => threadAction(t.threadId, action, acctParam).then(r => ({ threadId: t.threadId, changed: r?.data?.changed || [] }))))
        : await Promise.all(targets.map(t => updateEmail(t._id, { folder: action === "inbox" ? "inbox" : action }).then(() => ({ emailId: t._id, folder: t.folder }))));
      fetchFolderCounts();
      const n = targets.length;
      showSnack(`${n === 1 ? "Conversation" : `${n} conversations`} ${verb}.`, async () => {
        if (threaded) await Promise.all(results.map(r => threadAction(r.threadId, "restore", { ...acctParam, items: r.changed })));
        else await Promise.all(results.map(r => updateEmail(r.emailId, { folder: r.folder })));
        fetchFolderCounts();
        fetchRows();
      });
    } catch (e) {
      console.error(e);
      fetchRows();
    }
  };

  const deleteForeverRows = async (ids) => {
    if (!window.confirm(`Permanently delete ${ids.length === 1 ? "this conversation" : `${ids.length} conversations`}? This cannot be undone.`)) return;
    const targets = rows.filter(r => ids.includes(rowId(r)));
    try {
      await Promise.all(targets.map(t => threaded ? threadAction(t.threadId, "deleteForever", acctParam) : deleteEmail(t._id, true)));
      setRows(prev => prev.filter(r => !ids.includes(rowId(r))));
      setPagination(p => ({ ...p, total: Math.max(0, (p.total || 0) - targets.length) }));
      setSelectedRows([]);
      targets.forEach(t => convCache.current.delete(t.threadId));
      fetchFolderCounts();
      showSnack("Deleted forever.");
    } catch (e) { console.error(e); }
  };

  const markRows = async (ids, isRead) => {
    const targets = rows.filter(r => ids.includes(rowId(r)));
    setRows(prev => prev.map(r => ids.includes(rowId(r)) ? { ...r, isRead, hasUnread: !isRead } : r));
    setSelectedRows([]);
    targets.forEach(t => convCache.current.delete(t.threadId));
    try {
      await Promise.all(targets.map(t => threaded ? threadAction(t.threadId, isRead ? "read" : "unread", acctParam) : updateEmail(t._id, { isRead })));
      fetchFolderCounts();
    } catch (e) { console.error(e); fetchRows(); }
  };

  const conversationAction = async (action, verb) => {
    if (!conversation) return;
    try {
      if (conversation.single) {
        // A draft: plain per-message behaviour
        const m = conversation.messages[0];
        if (action === "trash") await deleteEmail(m._id, folder === "trash");
        else if (action === "deleteForever") await deleteEmail(m._id, true);
        else if (action === "unread") await updateEmail(m._id, { isRead: false });
        closeDetail(); fetchRows(); fetchFolderCounts();
        return;
      }
      if (action === "deleteForever" && !window.confirm("Permanently delete this conversation? This cannot be undone.")) return;
      const tid = conversation.threadId;
      convCache.current.delete(tid);
      const r = await threadAction(tid, action, acctParam);
      if (!r?.success) { alert(r?.error || "Action failed"); return; }
      const changed = r.data?.changed || [];
      closeDetail();
      fetchRows();
      fetchFolderCounts();
      showSnack(`Conversation ${verb}.`, changed.length ? async () => {
        await threadAction(tid, "restore", { ...acctParam, items: changed });
        fetchRows(); fetchFolderCounts();
      } : undefined);
    } catch (e) { console.error(e); }
  };

  const handleDeleteAll = async () => {
    const total = pagination.total || 0;
    if (total === 0) return;

    const acctLabel = selectedAccount
      ? (emailAccounts.find(a => a._id === selectedAccount)?.emailAddress || "this account")
      : "ALL accounts";
    const willPermanent = folder === "trash" || folder === "spam";
    const verb = willPermanent ? "permanently delete" : "move to trash";

    if (!window.confirm(
      `${verb.toUpperCase()} everything in "${folder}" for ${acctLabel}?\n\n` +
      (willPermanent ? "This cannot be undone." : "You can restore them from Trash.")
    )) return;

    try {
      setRefreshing(true);
      await bulkDeleteEmails({
        emailAccountId: selectedAccount || undefined,
        folder,
        permanent:      willPermanent,
      });
      setSelectedRows([]);
      setConversation(null);
      convCache.current.clear();
      setPage(1);
      await fetchRows();
      fetchFolderCounts();   // keep sidebar badges in sync after delete-all
    } catch (e) {
      console.error("bulk delete failed:", e);
      alert("Delete all failed: " + (e?.error || e?.message || "unknown error"));
    } finally {
      setRefreshing(false);
    }
  };

  // Switching mailbox (or back to All) restarts the list the way changing
  // folder does — otherwise page 3 of one mailbox becomes page 3 of another,
  // with a message from the mailbox you just left still open beside it.
  const selectAccount = (id) => {
    setSelectedAccount(id);
    setPage(1);
    setSelectedRows([]);
    setConversation(null);
  };

  const changeFolder = (newFolder, newStarred = null) => {
    setFolder(newFolder);
    setFilterStarred(newStarred);
    setFilterRead(null);
    setFilterCampaign(null);
    setPage(1);
    setConversation(null);
    setReplyTarget(null);
    setForwardTarget(null);
    setReplyText("");
    setSelectedRows([]);
  };

  // ── Sidebar config ─────────────────────────────────────────────────────────
  const sidebarItems = [
    { key: "inbox",   label: "Inbox",   icon: <InboxIcon   fontSize="small" />, folder: "inbox"  },
    { key: "starred", label: "Starred", icon: <StarIcon    fontSize="small" />, folder: "inbox", starFilter: true },
    { key: "sent",    label: "Sent",    icon: <SendIcon    fontSize="small" />, folder: "sent"   },
    { key: "draft",   label: "Drafts",  icon: <DraftsIcon  fontSize="small" />, folder: "draft"  },
    { key: "archive", label: "Archive", icon: <ArchiveIcon fontSize="small" />, folder: "archive" },
    { key: "spam",    label: "Spam",    icon: <ReportIcon  fontSize="small" />, folder: "spam"   },
    { key: "trash",   label: "Trash",   icon: <DeleteIcon  fontSize="small" />, folder: "trash"  },
  ];
  const isActive = (item) =>
    item.key === "starred"
      ? filterStarred === true
      : folder === item.folder && !filterStarred;

  // Badge = unread conversations (Gmail counts conversations, not messages)
  const getUnread = (f) => folderCounts[f]?.unreadThreads ?? folderCounts[f]?.unread ?? 0;

  const inTrashView = folder === "trash" || folder === "spam";
  // Warm-up mail is read-only: Instantly.ai answers it, and moving or
  // deleting it could get in the way of the warm-up.
  const isWarmupView = folder === "warmup";
  const isSentLike = folder === "sent" || folder === "draft";
  const canArchive = !["archive", "trash", "spam"].includes(folder) && !isSentLike && !filterStarred;

  const anyUnreadSelected = rows.some(r => selectedRows.includes(rowId(r)) && rowUnread(r));


  const StatusChip = ({ status, error, emailId }) => {
    if (isPending(status)) return <span className={`${styles.statusChip} ${styles.statusSending}`}>Sending…</span>;
    if (status === "failed") return (
      <span className={`${styles.statusChip} ${styles.statusFailed}`} title={error || "Not sent"}>
        <ErrorOutlineIcon style={{ fontSize: 14 }} /> Not sent
        {emailId && <button className={styles.retryLink} onClick={(e) => { e.stopPropagation(); retry(emailId); }}>Retry</button>}
      </span>
    );
    return null;
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className={styles.gmailContainer}>

      {/* ── Sidebar ─────────────────────────────────────────────────────── */}
      <aside className={styles.sidebar}>
        <button
          className={styles.composeBtn}
          onClick={() => { setShowCompose(true); setComposeMinimized(false); }}
          disabled={emailAccounts.length === 0}
        >
          <EditIcon fontSize="small" />
          <span className={styles.composeBtnLabel}>Compose</span>
        </button>

        <nav className={styles.sidebarNav}>
          {sidebarItems.map(item => (
            <button
              key={item.key}
              className={`${styles.sidebarItem} ${isActive(item) ? styles.sidebarActive : ""}`}
              onClick={() => changeFolder(item.folder, item.starFilter || null)}
            >
              <span className={styles.sidebarIcon}>{item.icon}</span>
              <span className={styles.sidebarLabel}>{item.label}</span>
              {item.key !== "starred" && getUnread(item.folder) > 0 && (
                <span className={styles.sidebarBadge}>{getUnread(item.folder)}</span>
              )}
            </button>
          ))}
        </nav>

        {emailAccounts.length > 0 && (
          <div className={styles.sidebarAccounts}>
            <div className={styles.sidebarSectionHead}>
              <span className={styles.sidebarSectionLabel}>Accounts</span>
              {/* Picking a mailbox filters the list; this is the way back to
                  every mailbox, which was only a second click on the same row */}
              <button
                className={`${styles.acctAllBtn} ${selectedAccount ? "" : styles.acctAllOn}`}
                onClick={() => selectAccount("")}
                title="Show mail from every mailbox"
              >
                All
              </button>
            </div>
            {emailAccounts.map(acc => {
              const unread = accountCounts[acc._id]?.unreadThreads || 0;
              const [local, domain] = String(acc.emailAddress).split("@");
              return (
                <button
                  key={acc._id}
                  className={`${styles.sidebarAccountBtn} ${selectedAccount === acc._id ? styles.sidebarActive : ""}`}
                  onClick={() => selectAccount(selectedAccount === acc._id ? "" : acc._id)}
                  title={unread ? `${acc.emailAddress} — ${unread} unread` : acc.emailAddress}
                >
                  <div className={styles.miniAvatar}>
                    {acc.emailAddress[0].toUpperCase()}
                  </div>
                  {/* Two lines: several mailboxes share a local part (two
                      postmaster@, two info@), so the domain has to be visible */}
                  <span className={styles.acctLabel}>
                    <span className={styles.acctLocal}>{local}</span>
                    <span className={styles.acctDomain}>@{domain}</span>
                  </span>
                  {unread > 0 && <span className={styles.sidebarBadge}>{unread}</span>}
                </button>
              );
            })}
          </div>
        )}
      </aside>

      {/* ── Main content ─────────────────────────────────────────────────── */}
      <div className={styles.mainContent}>

        {/* Search bar */}
        <div className={styles.searchBar}>
          <SearchIcon className={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search in mail"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className={styles.searchInput}
          />
          {searchQuery && (
            <button className={styles.clearSearch} onClick={() => setSearchQuery("")}>
              <CloseIcon fontSize="small" />
            </button>
          )}
        </div>

        {/* ── Conversation list ─────────────────────────────────────────── */}
        {!conversation && (
          <div className={`${styles.listView} ${styles.fadeIn}`}>

            {/* Toolbar */}
            <div className={styles.toolbar}>
              <div className={styles.toolbarLeft}>
                {!isWarmupView && <label className={styles.selectAllWrap}>
                  <input
                    type="checkbox"
                    className={styles.selectAllCheck}
                    checked={selectedRows.length === rows.length && rows.length > 0}
                    onChange={e =>
                      setSelectedRows(e.target.checked ? rows.map(rowId) : [])
                    }
                  />
                </label>}

                {selectedRows.length > 0 ? (
                  <div className={styles.bulkBar}>
                    {canArchive && (
                      <button className={styles.tbBtn} onClick={() => moveRows(selectedRows, "archive", "archived")} title="Archive">
                        <ArchiveIcon fontSize="small" />
                      </button>
                    )}
                    {folder === "spam" ? (
                      <button className={styles.tbBtn} onClick={() => moveRows(selectedRows, "inbox", "moved to Inbox")} title="Not spam">
                        <InboxIcon fontSize="small" />
                      </button>
                    ) : folder !== "trash" && !isSentLike && (
                      <button className={styles.tbBtn} onClick={() => moveRows(selectedRows, "spam", "marked as spam")} title="Report spam">
                        <ReportIcon fontSize="small" />
                      </button>
                    )}
                    {folder === "trash" ? (
                      <>
                        <button className={styles.tbBtn} onClick={() => moveRows(selectedRows, "inbox", "moved to Inbox")} title="Move to Inbox">
                          <InboxIcon fontSize="small" />
                        </button>
                        <button className={styles.tbBtn} onClick={() => deleteForeverRows(selectedRows)} title="Delete forever">
                          <DeleteIcon fontSize="small" />
                        </button>
                      </>
                    ) : folder === "spam" ? (
                      // Spam is deleted permanently — it never needs a second stop in Trash.
                      <button className={styles.tbBtn} onClick={() => deleteForeverRows(selectedRows)} title="Delete forever">
                        <DeleteIcon fontSize="small" />
                      </button>
                    ) : (
                      <button className={styles.tbBtn} onClick={() => moveRows(selectedRows, "trash", "moved to Trash")} title="Delete">
                        <DeleteIcon fontSize="small" />
                      </button>
                    )}
                    {anyUnreadSelected ? (
                      <button className={styles.tbBtn} onClick={() => markRows(selectedRows, true)} title="Mark as read">
                        <MarkEmailReadIcon fontSize="small" />
                      </button>
                    ) : (
                      <button className={styles.tbBtn} onClick={() => markRows(selectedRows, false)} title="Mark as unread">
                        <MarkEmailUnreadIcon fontSize="small" />
                      </button>
                    )}
                    <span className={styles.bulkCount}>{selectedRows.length} selected</span>
                  </div>
                ) : (
                  <>
                    <button
                      className={`${styles.tbBtn} ${refreshing ? styles.spinning : ""}`}
                      onClick={() => { setRefreshing(true); fetchRows().finally(() => setRefreshing(false)); fetchFolderCounts(); }}
                      title="Refresh"
                    >
                      <RefreshIcon fontSize="small" />
                    </button>
                    {!isWarmupView && <div className={styles.filterTabs}>
                      <button
                        className={!filterRead && !filterStarred && !filterCampaign ? styles.activeTab : styles.tabBtn}
                        onClick={() => { setFilterRead(null); setFilterStarred(null); setFilterCampaign(null); setPage(1); }}
                      >All</button>
                      <button
                        className={filterRead === false ? styles.activeTab : styles.tabBtn}
                        onClick={() => setFilterRead(prev => prev === false ? null : false)}
                      >Unread</button>
                      <button
                        className={filterStarred ? styles.activeTab : styles.tabBtn}
                        onClick={() => setFilterStarred(prev => prev ? null : true)}
                      >Starred</button>
                    </div>}
                  </>
                )}
              </div>

              <div className={styles.toolbarRight}>
                {!isWarmupView && <button
                  className={styles.tbBtn}
                  onClick={handleDeleteAll}
                  disabled={!pagination.total || refreshing}
                  title={folder === "trash"
                    ? `Permanently delete everything in trash`
                    : `Move everything in ${folder} to trash`}
                  style={pagination.total ? { color: "#c0392b" } : undefined}
                >
                  <DeleteSweepIcon fontSize="small" />
                </button>}
                {pagination.total > 0 && (
                  <span className={styles.pageInfo}>
                    {(page - 1) * 50 + 1}–{Math.min(page * 50, pagination.total)} of {pagination.total}
                  </span>
                )}
                <button className={styles.tbBtn} onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
                  <ChevronLeftIcon fontSize="small" />
                </button>
                <button className={styles.tbBtn} onClick={() => setPage(p => Math.min(pagination.pages, p + 1))} disabled={page >= pagination.pages}>
                  <ChevronRightIcon fontSize="small" />
                </button>
              </div>
            </div>

            {/* Rows stay put while refreshing — only a thin bar moves */}
            {loading && rows.length > 0 && <div className={styles.progressBar} />}

            {isWarmupView && (
              <div style={{ padding: "8px 16px", fontSize: "0.8rem", opacity: 0.75, borderBottom: "1px solid rgba(128,128,128,0.2)" }}>
                Warm-up emails from Instantly.ai's Standard Pool land here instead of your Inbox. They're answered automatically, so there's nothing you need to do.
              </div>
            )}
            {(folder === "spam" || folder === "trash") && (
              <div style={{ padding: "8px 16px", fontSize: "0.8rem", opacity: 0.75, borderBottom: "1px solid rgba(128,128,128,0.2)" }}>
                {folder === "spam"
                  ? "Messages in Spam are deleted automatically after 30 days. Deleting a message here removes it permanently."
                  : "Messages in Trash are deleted automatically after 30 days."}
              </div>
            )}

            {/* A failed send must be impossible to miss: the row chip and the
                bell notification are easy to overlook, so the Sent list also
                opens with a red bar while any message in view was not sent. */}
            {folder === "sent" && rows.some(r => r.hasFailed) && (() => {
              const n = rows.filter(r => r.hasFailed).length;
              return (
                <div role="alert" style={{ padding: "10px 16px", fontSize: "0.82rem", background: "#fce8e6", color: "#c5221f", borderBottom: "1px solid rgba(197,34,31,0.25)", display: "flex", alignItems: "center", gap: 8 }}>
                  <ErrorOutlineIcon style={{ fontSize: 16 }} />
                  <span><strong>{n === 1 ? "1 message was not sent." : `${n} messages were not sent.`}</strong> They are marked "Not sent" below — open one to see why, fix the wording and use Retry.</span>
                </div>
              );
            })()}

            <div className={styles.emailList}>
              {loading && rows.length === 0 ? (
                <div className={styles.loadingState}>
                  <div className={styles.spinner} />
                  <span>Loading…</span>
                </div>
              ) : rows.length === 0 ? (
                <div className={styles.emptyState}>
                  <EmailIcon className={styles.emptyIcon} />
                  <p>{searchQuery ? "No conversations matched your search" : isWarmupView ? "No warm-up emails yet. They appear here once warm-up starts for your inboxes." : `No conversations in ${folder}`}</p>
                </div>
              ) : (
                rows.map(row => {
                  const id = rowId(row);
                  const unread = rowUnread(row);
                  const hasAtt = threaded ? row.hasAttachments : (row.attachments || []).length > 0;
                  const date = threaded ? row.latestDate : row.date;
                  const subject = threaded ? row.subject : (row.subject || "(No Subject)");
                  const snip = threaded ? row.snippet : snippetOf(row);

                  // Participants column, like Gmail: "Priya, me 3" — or "To: X" when I'm the only sender
                  let who;
                  if (threaded) {
                    const others = row.participants.filter(p => !p.me);
                    if (!others.length || isSentLike) {
                      who = <span className={unread ? styles.pUnread : ""}>To: {row.toNames?.length ? row.toNames.join(", ") : "me"}</span>;
                    } else {
                      who = row.participants.map((p, i) => (
                        <span key={i} className={p.unread ? styles.pUnread : styles.pRead}>
                          {i > 0 ? ", " : ""}{row.participants.length > 1 ? firstName(p) : (p.me ? "me" : (p.name || p.email))}
                        </span>
                      ));
                    }
                  } else {
                    who = <span className={unread ? styles.pUnread : ""}>To: {recipientsOf(row) || "(no recipient)"}</span>;
                  }
                  const avatarSeed = threaded
                    ? (row.participants.find(p => !p.me)?.email || row.emailAddress)
                    : recipientsOf(row);
                  const avatarName = threaded
                    ? (row.participants.find(p => !p.me)?.name || row.toNames?.[0] || "me")
                    : recipientsOf(row);

                  return (
                    <div
                      key={id}
                      className={`${styles.emailRow} ${unread ? styles.unread : styles.read} ${selectedRows.includes(id) ? styles.selectedRow : ""}`}
                      onClick={() => openRow(row)}
                      onMouseEnter={() => prefetchRow(row)}
                      onMouseLeave={cancelPrefetch}
                    >
                      <div className={styles.rowControls} onClick={e => e.stopPropagation()}>
                        {!isWarmupView && <><input
                          type="checkbox"
                          className={styles.rowCheck}
                          checked={selectedRows.includes(id)}
                          onChange={() =>
                            setSelectedRows(prev =>
                              prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
                            )
                          }
                        />
                        <button className={styles.rowStarBtn} onClick={(e) => toggleStarRow(row, e)}>
                          {row.isStarred
                            ? <StarIcon fontSize="small" className={styles.starOn} />
                            : <StarBorderIcon fontSize="small" className={styles.starOff} />
                          }
                        </button></>}
                      </div>
                      <div className={styles.rowAvatar} style={{ background: colorFor(avatarSeed) }}>
                        {initialOf(avatarName)}
                      </div>
                      <div className={styles.rowParticipants}>
                        {who}
                        {threaded && row.emailCount > 1 && <span className={styles.rowCount}>{row.emailCount}</span>}
                        {threaded && !selectedAccount && emailAccounts.length > 1 && row.emailAddress && (
                          <span className={styles.rowAcct}>{row.emailAddress.split("@")[0]}</span>
                        )}
                      </div>
                      <div className={styles.rowBody}>
                        <span className={styles.rowSubject}>{subject}</span>
                        <span className={styles.rowPreview}>{" — "}{snip}</span>
                      </div>
                      {threaded && row.hasPending && <span className={`${styles.statusChip} ${styles.statusSending}`}>Sending…</span>}
                      {threaded && row.hasFailed && <span className={`${styles.statusChip} ${styles.statusFailed}`}><ErrorOutlineIcon style={{ fontSize: 14 }} /> Not sent</span>}
                      {threaded && row.unsubscribed && <span className={`${styles.statusChip} ${styles.statusUnsub}`} title={UNSUB_TITLE}>Unsubscribed</span>}
                      {hasAtt && <span className={styles.rowClip}><AttachFileIcon style={{ fontSize: 16 }} /></span>}
                      <div className={styles.rowDate}>{formatDate(date)}</div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* ── Conversation view ─────────────────────────────────────────── */}
        {conversation && (() => {
          const all = conversation.messages;
          const visible = all.filter(m => inTrashView || conversation.single || !HIDDEN_IN_CONVERSATION.includes(m.folder));
          const hidden  = all.filter(m => !inTrashView && !conversation.single && HIDDEN_IN_CONVERSATION.includes(m.folder));
          const shown   = showHidden ? all : visible;
          const last    = visible[visible.length - 1] || all[all.length - 1];
          const folders = [...new Set(visible.map(m => m.folder))];
          const toggleExpand = (id) => setExpanded(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
          const toggleDetails = (id) => setDetails(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

          return (
            <div className={`${styles.detailView} ${styles.fadeIn}`}>
              {/* Detail toolbar */}
              <div className={styles.detailToolbar}>
                <button className={styles.backBtn} onClick={closeDetail} title="Back">
                  <ArrowBackIcon fontSize="small" />
                </button>
                <div className={styles.detailActions}>
                  {!isWarmupView && <>
                  {!conversation.single && !inTrashView && folders.includes("inbox") && (
                    <button className={styles.actionBtn} onClick={() => conversationAction("archive", "archived")} title="Archive">
                      <ArchiveIcon fontSize="small" /> Archive
                    </button>
                  )}
                  {!conversation.single && (folder === "spam" ? (
                    <button className={styles.actionBtn} onClick={() => conversationAction("inbox", "moved to Inbox")} title="Not spam">
                      <InboxIcon fontSize="small" /> Not spam
                    </button>
                  ) : !inTrashView && !visible.every(isMine) && (
                    <button className={styles.actionBtn} onClick={() => conversationAction("spam", "marked as spam")} title="Report spam">
                      <ReportIcon fontSize="small" /> Spam
                    </button>
                  ))}
                  {folder === "trash" ? (
                    <>
                      {!conversation.single && (
                        <button className={styles.actionBtn} onClick={() => conversationAction("inbox", "moved to Inbox")} title="Move to Inbox">
                          <InboxIcon fontSize="small" /> Move to Inbox
                        </button>
                      )}
                      <button className={`${styles.actionBtn} ${styles.deleteBtn}`} onClick={() => conversationAction("deleteForever", "deleted forever")} title="Delete forever">
                        <DeleteIcon fontSize="small" /> Delete forever
                      </button>
                    </>
                  ) : folder === "spam" ? (
                    <button className={`${styles.actionBtn} ${styles.deleteBtn}`} onClick={() => conversationAction("deleteForever", "deleted forever")} title="Delete forever">
                      <DeleteIcon fontSize="small" /> Delete forever
                    </button>
                  ) : (
                    <button className={`${styles.actionBtn} ${styles.deleteBtn}`} onClick={() => conversationAction("trash", "moved to Trash")} title="Delete">
                      <DeleteIcon fontSize="small" />
                    </button>
                  )}
                  <button className={styles.actionBtn} onClick={() => conversationAction("unread", "marked as unread")} title="Mark unread">
                    <MarkEmailUnreadIcon fontSize="small" />
                  </button>
                  </>}
                  {visible.length > 1 && (
                    <button
                      className={styles.convExpandAll}
                      onClick={() => setExpanded(expanded.size === visible.length ? new Set([last._id]) : new Set(visible.map(m => m._id)))}
                    >
                      {expanded.size === visible.length ? "Collapse all" : "Expand all"}
                    </button>
                  )}
                </div>
              </div>

              {/* Scrollable content */}
              <div className={styles.detailContent}>
                <div className={styles.convHead}>
                  <h2 className={styles.detailSubject}>{conversationSubject(all[0]?.subject)}</h2>
                  {conversation.single
                    ? <span className={styles.convChip}>Draft</span>
                    : folders.map(f => <span key={f} className={styles.convChip}>{f}</span>)}
                  {visible.length > 1 && <span className={styles.convCount}>{visible.length} messages</span>}
                </div>

                {shown.map((m) => {
                  const isOpen = expanded.has(m._id) || shown.length === 1;
                  const mine = isMine(m);
                  const hiddenKind = HIDDEN_IN_CONVERSATION.includes(m.folder) && !inTrashView;
                  const acct = typeof m.emailAccountId === "object" ? m.emailAccountId?.emailAddress : accountLabel(m.emailAccountId);
                  const toLine = (m.to || []).map(t => ownAddresses.has(String(t.email || "").toLowerCase()) ? "me" : (t.name || t.email)).join(", ");

                  // Gmail shows the sender's NAME on your own messages here ("me"
                  // only appears in the list rows and the "to me" line).
                  const who = getSenderName(m);
                  const isLastVisible = m._id === last?._id;
                  // The newest message stays open while it is the only one open —
                  // collapsing everything leaves nothing to read.
                  const canCollapse = shown.length > 1 && !(isLastVisible && expanded.size <= 1);

                  if (!isOpen) {
                    return (
                      <div key={m._id} role="button" tabIndex={0} className={`${styles.convCollapsed} ${hiddenKind ? styles.convMuted : ""} ${styles.fadeIn}`}
                        onClick={() => toggleExpand(m._id)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") toggleExpand(m._id); }}>
                        <div className={styles.convAvatar} style={{ background: colorFor(m.from?.email) }}>{initialOf(who)}</div>
                        <div className={styles.convCollapsedMain}>
                          <div className={styles.convCollapsedTop}>
                            <span className={`${styles.convCollapsedFrom} ${!m.isRead ? styles.pUnread : ""}`}>{who}</span>
                            <StatusChip status={m.deliveryStatus} error={m.deliveryError} />
                            {hiddenKind && <span className={styles.convChip}>{m.folder}</span>}
                            {m.unsubscribe?.at && <span className={`${styles.statusChip} ${styles.statusUnsub}`} title={UNSUB_TITLE}>Unsubscribed</span>}
                            <span className={styles.convCollapsedDate}>{gmailDate(m.date)}</span>
                            {!isWarmupView && <button className={styles.convIconBtn} onClick={(e) => { e.stopPropagation(); toggleStarMessage(m); }} title={m.isStarred ? "Unstar" : "Star"}>
                              {m.isStarred ? <StarIcon className={styles.starOn} /> : <StarBorderIcon />}
                            </button>}
                          </div>
                          <div className={styles.convCollapsedSnip}>
                            {cleanSnippet(m)}
                            {(m.attachments || []).length > 0 && <AttachFileIcon style={{ fontSize: 15, color: "#5f6368", marginLeft: 6, verticalAlign: "middle" }} />}
                          </div>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div key={m._id} className={`${styles.emailCard} ${styles.convCard} ${hiddenKind ? styles.convMuted : ""} ${styles.fadeIn}`}>
                      <div className={styles.cardAvatar} style={{ background: colorFor(m.from?.email) }}>
                        {initialOf(who)}
                      </div>
                      <div className={styles.cardBody}>
                        <div
                          className={`${styles.cardHeader} ${canCollapse ? styles.convMsgHeadClickable : ""}`}
                          onClick={(e) => { if (e.target.closest("button")) return; if (canCollapse) toggleExpand(m._id); }}
                        >
                          <div className={styles.cardFromRow}>
                            <strong className={styles.cardSenderName}>{who}</strong>
                            <span className={styles.cardSenderEmail}>&lt;{m.from?.email}&gt;</span>
                            <StatusChip status={m.deliveryStatus} error={m.deliveryError} emailId={mine ? m._id : null} />
                            {hiddenKind && <span className={styles.convChip}>{m.folder}</span>}
                            {m.unsubscribe?.at && <span className={`${styles.statusChip} ${styles.statusUnsub}`} title={UNSUB_TITLE}>Unsubscribed</span>}
                            {!selectedAccount && emailAccounts.length > 1 && acct && <span className={styles.rowAcct}>{acct}</span>}
                          </div>
                          <div className={styles.convMsgActions}>
                            <span className={styles.cardDate}>{gmailDate(m.date)}</span>
                            {!isWarmupView && <button className={styles.convIconBtn} onClick={() => toggleStarMessage(m)} title={m.isStarred ? "Unstar" : "Star"}>
                              {m.isStarred ? <StarIcon className={styles.starOn} /> : <StarBorderIcon />}
                            </button>}
                            {!conversation.single && !isWarmupView && (
                              <>
                                <button className={styles.convIconBtn} onClick={() => startReply(m, "reply")} title="Reply"><ReplyIcon /></button>
                                <button className={styles.convIconBtn} onClick={() => startForward(m)} title="Forward"><ForwardToInboxIcon /></button>
                              </>
                            )}
                          </div>
                        </div>
                        {m.deliveryStatus === "failed" && m.deliveryError && (
                          <div className={styles.composeError} style={{ margin: "4px 0 8px" }}>{m.deliveryError}</div>
                        )}
                        <div className={styles.cardTo}>
                          <button className={styles.cardToBtn} onClick={() => toggleDetails(m._id)}>
                            to {toLine || "—"} <ExpandMoreIcon style={{ fontSize: 16 }} />
                          </button>
                          {details.has(m._id) && (
                            <dl className={styles.convDetails}>
                              <dt>from:</dt><dd>{getSenderName(m)} &lt;{m.from?.email}&gt;</dd>
                              <dt>to:</dt><dd>{(m.to || []).map(t => t.name ? `${t.name} <${t.email}>` : t.email).join(", ") || "—"}</dd>
                              {m.cc?.length > 0 && <><dt>cc:</dt><dd>{m.cc.map(c => c.email).join(", ")}</dd></>}
                              <dt>date:</dt><dd>{new Date(m.date).toLocaleString()}</dd>
                              <dt>subject:</dt><dd>{m.subject || "(No Subject)"}</dd>
                              {acct && <><dt>mailbox:</dt><dd>{acct}</dd></>}
                            </dl>
                          )}
                        </div>

                        {/* Body — sanitized with DOMPurify; quoted earlier messages sit behind "•••" */}
                        <div className={styles.cardEmailBody}>
                          <MessageBody html={m.html} text={m.text} />
                        </div>

                        {/* Attachments */}
                        {m.attachments?.length > 0 && (
                          <div className={styles.attachSection}>
                            <div className={styles.attachTitle}>
                              <AttachFileIcon fontSize="small" />
                              {m.attachments.length} attachment{m.attachments.length > 1 ? "s" : ""}
                            </div>
                            <div className={styles.attachList}>
                              {m.attachments.map((att, i) => (
                                <button
                                  key={i}
                                  className={`${styles.attachChip} ${styles.attachChipBtn}`}
                                  title="Click to download"
                                  onClick={() => downloadAttachment(m._id, i, att.filename)}
                                >
                                  <AttachFileIcon fontSize="small" />
                                  <span>{att.filename}</span>
                                  <span className={styles.attachSize}>({(att.size / 1024).toFixed(1)} KB)</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}

                {hidden.length > 0 && (
                  <button className={styles.convHiddenNote} onClick={() => setShowHidden(v => !v)}>
                    {hidden.length} {hidden.length === 1 ? "message" : "messages"} in {hidden.every(m => m.folder === "spam") ? "Spam" : "Trash"} · {showHidden ? "Hide" : "Show"}
                  </button>
                )}

                {/* Forward compose box */}
                {forwardTarget && (
                  <div className={styles.inlineReply} ref={replyBoxRef}>
                    <div className={styles.replyHeader}>
                      <ForwardToInboxIcon fontSize="small" />
                      <span>Forward: <strong>{forwardTarget.subject}</strong></span>
                    </div>
                    <div className={styles.composeField} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0" }}>
                      <span className={styles.composeLabel}>To</span>
                      <input
                        type="text"
                        className={styles.composeInput}
                        value={forwardTo}
                        onChange={e => setForwardTo(e.target.value)}
                        placeholder="Recipient email(s), comma-separated"
                        style={{ flex: 1 }}
                      />
                    </div>
                    <textarea
                      className={styles.replyTextarea}
                      value={forwardNote}
                      onChange={e => setForwardNote(e.target.value)}
                      placeholder="Add a note (optional)…"
                      rows={3}
                    />
                    <div className={styles.replyFooter}>
                      <button
                        className={styles.sendReplyBtn}
                        onClick={handleForward}
                        disabled={sendingForward || !forwardTo.trim()}
                      >
                        <ForwardToInboxIcon fontSize="small" />
                        {sendingForward ? "Forwarding…" : "Forward"}
                      </button>
                      <button
                        className={styles.discardReplyBtn}
                        onClick={() => { setForwardTarget(null); setForwardTo(""); setForwardNote(""); }}
                      >
                        <CloseIcon fontSize="small" /> Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* Inline reply box */}
                {replyTarget ? (
                  <div className={styles.inlineReply} ref={replyBoxRef}>
                    <div className={styles.replyHeader}>
                      {replyMode === "replyAll" ? <ReplyAllIcon fontSize="small" /> : <ReplyIcon fontSize="small" />}
                      <span>
                        {replyMode === "replyAll" ? "Reply All to " : "Reply to "}
                        <strong>{isMine(replyTarget) ? (recipientsOf(replyTarget) || "me") : getSenderName(replyTarget)}</strong>
                        {/* Replying to my own sent message goes to its recipients (as in Gmail) */}
                        <span className={styles.replyToAddr}>
                          &lt;{isMine(replyTarget) ? ((replyTarget.to || []).map(t => t.email).join(", ") || replyTarget.from?.email) : replyTarget.from?.email}&gt;
                        </span>
                        {replyMode === "replyAll" && replyTarget.to?.length > 0 && (
                          <span className={styles.replyToAddr}> + {replyTarget.to.length} recipient{replyTarget.to.length > 1 ? "s" : ""}</span>
                        )}
                      </span>
                    </div>
                    <textarea
                      className={styles.replyTextarea}
                      value={replyText}
                      onChange={e => setReplyText(e.target.value)}
                      placeholder="Write your reply…"
                      autoFocus
                      rows={5}
                    />
                    <div className={styles.quotedBlock}>
                      <div className={styles.quotedMeta}>
                        On {fullDate(replyTarget.date)}, {getSenderName(replyTarget)} wrote:
                      </div>
                      <div className={styles.quotedText}>
                        {snippetOf(replyTarget).substring(0, 300)}
                      </div>
                    </div>
                    {replyAttachments.length > 0 && (
                      <div className={styles.replyAttachList}>
                        {replyAttachments.map((f, i) => (
                          <span key={i} className={styles.replyAttachChip} title={f.name}>
                            <AttachFileIcon fontSize="small" />
                            <span className={styles.replyAttachName}>{f.name}</span>
                            <span className={styles.attachSize}>({(f.size / 1024).toFixed(1)} KB)</span>
                            <button
                              type="button"
                              className={styles.replyAttachRemove}
                              onClick={() => removeReplyAttachment(i)}
                              title="Remove"
                              aria-label={`Remove ${f.name}`}
                            >
                              <CloseIcon style={{ fontSize: 14 }} />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                    <input
                      ref={replyFileInputRef}
                      type="file"
                      multiple
                      style={{ display: "none" }}
                      onChange={handleReplyAttach}
                    />
                    <div className={styles.replyFooter}>
                      <button
                        className={styles.sendReplyBtn}
                        onClick={handleReplyInline}
                        disabled={sendingReply || (!replyText.trim() && replyAttachments.length === 0)}
                      >
                        <SendIcon fontSize="small" />
                        {sendingReply ? "Sending…" : "Send"}
                      </button>
                      <button
                        className={styles.replyAttachBtn}
                        onClick={() => replyFileInputRef.current?.click()}
                        title="Attach files"
                        type="button"
                      >
                        <AttachFileIcon fontSize="small" />
                      </button>
                      <button
                        className={styles.discardReplyBtn}
                        onClick={() => { setReplyTarget(null); setReplyText(""); setReplyAttachments([]); }}
                      >
                        <DeleteIcon fontSize="small" /> Discard
                      </button>
                    </div>
                  </div>
                ) : (
                  !forwardTarget && !conversation.single && !isWarmupView && last && (
                    <div className={styles.quickReplyRow}>
                      <button className={styles.quickReplyBtn} onClick={() => startReply(last, "reply")}>
                        <ReplyIcon fontSize="small" /> Reply
                      </button>
                      {((last.to || []).length + (last.cc || []).length) > 1 && (
                        <button className={styles.quickReplyBtn} onClick={() => startReply(last, "replyAll")}>
                          <ReplyAllIcon fontSize="small" /> Reply All
                        </button>
                      )}
                      <button className={styles.quickReplyBtn} onClick={() => startForward(last)}>
                        <ForwardToInboxIcon fontSize="small" /> Forward
                      </button>
                    </div>
                  )
                )}
              </div>
            </div>
          );
        })()}
      </div>

      {/* ── Snackbar ──────────────────────────────────────────────────────── */}
      {snack && (
        <div style={{
          position: "fixed", left: 24, bottom: 24, zIndex: 1200,
          display: "flex", alignItems: "center", gap: 16,
          minWidth: 288, maxWidth: 560, padding: "12px 8px 12px 24px",
          background: "#323232", color: "#fff", borderRadius: 4,
          boxShadow: "0 3px 5px -1px rgba(0,0,0,.2), 0 6px 10px 0 rgba(0,0,0,.14)", fontSize: 14,
        }} role="status">
          <span style={{ flex: 1 }}>{snack.text}</span>
          {snack.action && (
            <button
              style={{ border: 0, background: "none", color: "#a8c7fa", fontWeight: 600, padding: "6px 8px", cursor: "pointer" }}
              onClick={async () => { const fn = snack.action.fn; setSnack(null); await fn(); }}
            >{snack.action.label}</button>
          )}
          <button style={{ border: 0, background: "none", color: "#fff", cursor: "pointer", padding: 6 }} onClick={() => setSnack(null)} aria-label="Dismiss">
            <CloseIcon fontSize="small" />
          </button>
        </div>
      )}

      {/* ── Floating Compose Window ───────────────────────────────────────── */}
      {showCompose && (
        <ComposeWindow
          emailAccounts={emailAccounts}
          defaultAccountId={selectedAccount || emailAccounts[0]?._id || ""}
          onSend={handleSendEmail}
          onClose={() => setShowCompose(false)}
          minimized={composeMinimized}
          onToggleMinimize={() => setComposeMinimized(v => !v)}
        />
      )}
    </div>
  );
};

// ─── Floating Compose Window ──────────────────────────────────────────────────
const ComposeWindow = ({ emailAccounts, defaultAccountId, onSend, onClose, minimized, onToggleMinimize }) => {
  const [fromId, setFromId]     = useState(defaultAccountId || emailAccounts[0]?._id || "");
  const [to, setTo]             = useState("");
  const [cc, setCc]             = useState("");
  const [bcc, setBcc]           = useState("");
  const [showCc, setShowCc]     = useState(false);
  const [showBcc, setShowBcc]   = useState(false);
  const [subject, setSubject]   = useState("");
  const [body, setBody]         = useState("");
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending]   = useState(false);
  const [error, setError]       = useState("");
  const [draftId, setDraftId]   = useState(null);
  const [draftStatus, setDraftStatus] = useState(""); // "saving" | "saved" | ""
  const autoSaveTimer = useRef(null);
  const fileInputRef = useRef(null);

  const parseList = (s) => s.split(",").map(e => ({ email: e.trim() })).filter(e => e.email);

  // Auto-save draft every 30s if there is any content
  useEffect(() => {
    const hasContent = to.trim() || subject.trim() || body.trim();
    clearTimeout(autoSaveTimer.current);
    if (!hasContent) return;
    autoSaveTimer.current = setTimeout(async () => {
      try {
        setDraftStatus("saving");
        const payload = {
          emailAccountId: fromId,
          to: parseList(to), cc: cc ? parseList(cc) : [],
          subject: subject || "",
          text: body, html: body.replace(/\n/g, "<br/>"),
        };
        if (draftId) {
          await updateDraft(draftId, payload);
        } else {
          const r = await createDraft(payload);
          if (r?.success && r.data?._id) setDraftId(r.data._id);
        }
        setDraftStatus("saved");
        setTimeout(() => setDraftStatus(""), 3000);
      } catch { setDraftStatus(""); }
    }, 30_000);
    return () => clearTimeout(autoSaveTimer.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [to, cc, subject, body, fromId, draftId]);

  const pickFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    const { accepted, message } = filterAttachments(attachments, picked);
    if (message) setError(message);
    setAttachments(prev => [...prev, ...accepted]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSend = async () => {
    setError("");
    if (!to.trim())   { setError("Recipient required"); return; }
    if (!body.trim() && attachments.length === 0) { setError("Message body required"); return; }
    if (!fromId)      { setError("Select a sender account"); return; }

    setSending(true);
    const result = await onSend({
      emailAccountId: fromId,
      to: parseList(to),
      cc: cc ? parseList(cc) : undefined,
      bcc: bcc ? parseList(bcc) : undefined,
      subject: subject || "(no subject)",
      text: body,
      html: body.replace(/\n/g, "<br/>"),
      attachments,
    });
    setSending(false);
    if (result?.success === false) {
      setError(result.error || "Failed to send");
    } else if (draftId) {
      // Discard the auto-saved draft now that email is sent
      discardDraft(draftId).catch(() => {});
    }
  };

  const handleDiscard = async () => {
    if (draftId) discardDraft(draftId).catch(() => {});
    onClose();
  };

  return (
    <div className={`${styles.composeWindow} ${minimized ? styles.composeMinimized : ""}`}>
      <div
        className={styles.composeHeader}
        onClick={minimized ? onToggleMinimize : undefined}
        style={minimized ? { cursor: "pointer" } : {}}
      >
        <span className={styles.composeTitle}>{minimized ? subject || "New Message" : "New Message"}</span>
        <div className={styles.composeControls}>
          <button onClick={e => { e.stopPropagation(); onToggleMinimize(); }} title={minimized ? "Expand" : "Minimize"}>
            <RemoveIcon fontSize="small" />
          </button>
          <button onClick={e => { e.stopPropagation(); handleDiscard(); }} title="Discard">
            <CloseIcon fontSize="small" />
          </button>
        </div>
      </div>

      {!minimized && (
        <>
          <div className={styles.composeFields}>
            {emailAccounts.length > 1 && (
              <div className={styles.composeField}>
                <span className={styles.composeLabel}>From</span>
                <select value={fromId} onChange={e => setFromId(e.target.value)} className={styles.composeSelect}>
                  {emailAccounts.map(a => (
                    <option key={a._id} value={a._id}>{a.emailAddress}</option>
                  ))}
                </select>
              </div>
            )}
            <div className={styles.composeField}>
              <span className={styles.composeLabel}>To</span>
              <input
                className={styles.composeInput}
                type="text"
                value={to}
                onChange={e => setTo(e.target.value)}
                placeholder="Recipients"
              />
              {!showCc && <button className={styles.ccBtn} onClick={() => setShowCc(true)}>Cc</button>}
              {!showBcc && <button className={styles.ccBtn} onClick={() => setShowBcc(true)}>Bcc</button>}
            </div>
            {showCc && (
              <div className={styles.composeField}>
                <span className={styles.composeLabel}>Cc</span>
                <input
                  className={styles.composeInput}
                  type="text"
                  value={cc}
                  onChange={e => setCc(e.target.value)}
                  placeholder="CC recipients"
                />
              </div>
            )}
            {showBcc && (
              <div className={styles.composeField}>
                <span className={styles.composeLabel}>Bcc</span>
                <input
                  className={styles.composeInput}
                  type="text"
                  value={bcc}
                  onChange={e => setBcc(e.target.value)}
                  placeholder="BCC recipients"
                />
              </div>
            )}
            <div className={styles.composeField}>
              <input
                className={`${styles.composeInput} ${styles.composeSubjectInput}`}
                type="text"
                value={subject}
                onChange={e => setSubject(e.target.value)}
                placeholder="Subject"
              />
            </div>
          </div>
          {error && <div className={styles.composeError}>{error}</div>}
          <textarea
            className={styles.composeBody}
            value={body}
            onChange={e => setBody(e.target.value)}
            placeholder="Write your message here…"
          />
          {attachments.length > 0 && (
            <div className={styles.composeAttachList}>
              {attachments.map((f, i) => (
                <span key={`${f.name}-${i}`} className={styles.replyAttachChip} title={f.name}>
                  <AttachFileIcon fontSize="small" />
                  <span className={styles.replyAttachName}>{f.name}</span>
                  <span className={styles.attachSize}>({(f.size / 1024).toFixed(1)} KB)</span>
                  <button type="button" className={styles.replyAttachRemove} onClick={() => setAttachments(prev => prev.filter((_, x) => x !== i))} aria-label={`Remove ${f.name}`}>
                    <CloseIcon style={{ fontSize: 14 }} />
                  </button>
                </span>
              ))}
            </div>
          )}
          <input ref={fileInputRef} type="file" multiple style={{ display: "none" }} onChange={pickFiles} />
          <div className={styles.composeFooter}>
            <div className={styles.composeFooterLeft}>
              <button
                className={styles.composeSendBtn}
                onClick={handleSend}
                disabled={sending || !to.trim() || (!body.trim() && attachments.length === 0)}
              >
                <SendIcon fontSize="small" /> {sending ? "Sending…" : "Send"}
              </button>
              <button className={styles.composeAttachBtn} onClick={() => fileInputRef.current?.click()} title="Attach files" type="button">
                <AttachFileIcon fontSize="small" />
              </button>
              {draftStatus === "saving" && <span className={styles.draftStatus}>Saving…</span>}
              {draftStatus === "saved"  && <span className={styles.draftStatus}>Draft saved</span>}
            </div>
            <button className={styles.composeDiscardBtn} onClick={handleDiscard} title="Discard">
              <DeleteIcon fontSize="small" />
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default Inbox;
